'use client';

import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { Button } from 'ui';
import DetailGuidance from '~/components/ItemDetails/DetailGuidance';
import FollowButton from '~/components/NewLockers/FollowButton';
import { useModals } from '~/contexts/ModalsContext';
import usePurchaseItem from '~/hooks/usePurchaseItem';
import FavoriteIcon from '~/public/static/svg/FavoriteIcon';
import { BundleIcon } from '~/public/static/svg/ItemActionIcons';
import {
  RailsDetailGuidance,
  RailsItem,
  RailsItemDetail,
} from '~/typings/services/rails/item';

export function AppDirItemPageFollowButton({ item }: { item: RailsItem }) {
  return (
    <FollowButton following={item.seller.following} userId={item.seller.id}>
      {(handleFollow, following) => (
        <button
          type="button"
          onClick={handleFollow}
          className="flex flex-1 items-center justify-center gap-2 px-4 py-3.5 text-sm font-semibold"
        >
          <FavoriteIcon fill="currentColor" height={18} active={following} />
          <span>{following ? 'Followed' : 'Follow'}</span>
        </button>
      )}
    </FollowButton>
  );
}

export function AppDirItemPageMessageSellerButton({
  item,
}: {
  item: RailsItem;
}) {
  const { onMessageSeller } = usePurchaseItem(item);

  return (
    <button
      type="button"
      className="flex flex-1 items-center justify-center gap-2 px-4 py-3.5 text-sm font-semibold"
      onClick={() => onMessageSeller()}
    >
      <ChatBubbleLeftEllipsisIcon className="h-5 w-5" />
      <span>Message Seller</span>
    </button>
  );
}

export function AppDirItemPageDetailGuidanceBuyingGuide({
  detail,
  guidance,
  item,
}: {
  detail: RailsItemDetail;
  guidance: RailsDetailGuidance;
  item: RailsItem;
}) {
  return (
    <DetailGuidance
      context="shop"
      guidance={guidance}
      detail={detail}
      category={item.categories[0]}
      customTrigger={handleOpen => (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            handleOpen();
          }}
          className="flex items-center gap-2 font-semibold"
        >
          <span>{guidance.guidance_link || `What is ${detail.name}?`}</span>
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </button>
      )}
    />
  );
}

export function AppDirItemDetailsBundleButton({ item }: { item: RailsItem }) {
  const modals = useModals();

  return (
    <Button
      onClick={() => modals.openModal('howToBundle', { itemId: item.id })}
      className="flex justify-center gap-2 text-center"
      fluid
      variant="outlined"
      size="large"
    >
      <BundleIcon className="h-5 w-5" />
      <span>Request Bundle</span>
    </Button>
  );
}

export function AppDirPublishedModal({ item }: { item: RailsItem }) {
  const modals = useModals();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    modals.openModal('itemPublished', { item });
    if (pathname) {
      router.replace(pathname);
    }
  }, []);

  return null;
}
